import { AdvisorSentiment } from "@types";

/**
 * The one file where a backend sentiment becomes a colour, in all three roles it plays: a
 * callout's accent, a table row's tint, and a badge's fill.
 *
 * Each family holds its own shade — icons at -500, badge text at -700 — because they sit on
 * different backgrounds and carry different weight. That difference is deliberate, not drift.
 */

/**
 * Callouts are supporting notes, so they read quietly: a coloured left rule and a matching icon
 * tint over neutral text — no fill. The colour still carries the sentiment; it just doesn't shout.
 */
export const CALLOUT_STYLES: Record<
  AdvisorSentiment,
  { accent: string; icon: string }
> = {
  positive: { accent: "border-emerald-400", icon: "text-emerald-500" },
  // slate isn't in this Tailwind version's colour module at all (unlike emerald/amber/sky,
  // which just needed wiring up in tailwind.config.js) — gray is the equivalent already used
  // everywhere else in this feature.
  neutral: { accent: "border-gray-300", icon: "text-gray-400" },
  warning: { accent: "border-amber-400", icon: "text-amber-500" },
  critical: { accent: "border-red-400", icon: "text-red-500" },
  info: { accent: "border-sky-400", icon: "text-sky-500" },
};

/** Row tints are lighter — a table of coloured rows should stay readable. */
export const ROW_STYLES: Record<AdvisorSentiment, string> = {
  positive: "bg-emerald-50 bg-opacity-60",
  neutral: "",
  warning: "bg-amber-50 bg-opacity-60",
  critical: "bg-red-50 bg-opacity-60",
  info: "bg-sky-50 bg-opacity-60",
};

export type BadgeVariant =
  | "success"
  | "muted"
  | "warning"
  | "critical"
  | "info";

export const BADGE_STYLES: Record<BadgeVariant, string> = {
  success: "border-emerald-200 bg-emerald-50 text-emerald-700",
  muted: "border-gray-200 bg-gray-100 text-gray-600",
  warning: "border-amber-200 bg-amber-50 text-amber-700",
  critical: "border-red-200 bg-red-50 text-red-700",
  info: "border-sky-200 bg-sky-50 text-sky-700",
};

export const SENTIMENT_BADGE_VARIANT: Record<AdvisorSentiment, BadgeVariant> = {
  positive: "success",
  neutral: "muted",
  warning: "warning",
  critical: "critical",
  info: "info",
};
